import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, UserRound, Building2, FileCheck2, MessageCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const Compare = ({ c }) => (
  <div className="grid sm:grid-cols-2 gap-4" data-testid="guide-compare">
    <div className="rounded-2xl border bg-white p-5">
      <p className="flex items-center gap-2 font-medium"><UserRound className="h-4 w-4 text-[#1E3A8A]" /> Fai da te</p>
      <dl className="mt-3 text-sm space-y-1.5">
        <div className="flex justify-between gap-2"><dt className="text-slate-500">Costo</dt><dd className="text-right">{c.diy.costo}</dd></div>
        <div className="flex justify-between gap-2"><dt className="text-slate-500">Tempo</dt><dd className="text-right">{c.diy.tempo}</dd></div>
        <div className="flex justify-between gap-2"><dt className="text-slate-500">Difficoltà</dt><dd className="text-right">{c.diy.difficolta}</dd></div>
      </dl>
    </div>
    <div className="rounded-2xl border bg-[#E07A5F]/5 border-[#E07A5F]/30 p-5">
      <p className="flex items-center gap-2 font-medium"><Building2 className="h-4 w-4 text-[#E07A5F]" /> CAF / Patronato</p>
      <dl className="mt-3 text-sm space-y-1.5">
        <div className="flex justify-between gap-2"><dt className="text-slate-500">Costo</dt><dd className="text-right">{c.caf.costo}</dd></div>
        <div className="flex justify-between gap-2"><dt className="text-slate-500">Tempo</dt><dd className="text-right">{c.caf.tempo}</dd></div>
      </dl>
      <p className="mt-3 text-sm text-slate-700"><span className="text-slate-500">Conviene se: </span>{c.caf.quando}</p>
    </div>
  </div>
);

export default function GuideDetail() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [done, setDone] = useState([]);
  useEffect(() => {
    api.get(`/guides/${slug}`).then(({ data }) => {
      setG(data);
      setDone(data.done || []);
    });
  }, [slug]);
  if (!g) return <div className="h-40 rounded-3xl bg-slate-100 animate-pulse" />;

  const toggle = (i) => {
    const next = done.includes(i) ? done.filter((x) => x !== i) : [...done, i];
    setDone(next);
    api.put(`/guides/${slug}/progress`, { done: next });
  };
  const pct = Math.round((done.length / g.steps.length) * 100);

  return (
    <div data-testid="guide-detail-page">
      <Link to="/app/guide" className="text-sm text-slate-600 flex items-center gap-1 mb-6 hover:text-slate-900" data-testid="guide-back-link"><ArrowLeft className="h-4 w-4" /> Tutte le guide</Link>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E07A5F]">{g.category}</p>
      <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight" data-testid="guide-title">{g.title}</h1>
      <p className="mt-2 text-slate-600 max-w-2xl">{g.summary}</p>
      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-3xl border bg-white p-6 md:p-8">
            <div className="flex items-center justify-between gap-4"><h2 className="text-lg font-medium">Checklist</h2><span className="text-sm text-slate-500" data-testid="guide-progress-label">{pct}% completato</span></div>
            <Progress value={pct} className="mt-3" />
            <ol className="mt-6 space-y-4">
              {g.steps.map((s, i) => (
                <li key={i} className={`flex gap-4 rounded-2xl p-4 transition-colors ${done.includes(i) ? "bg-emerald-50/60" : "bg-slate-50"}`}>
                  <Checkbox checked={done.includes(i)} onCheckedChange={() => toggle(i)} className="mt-1" data-testid={`guide-step-${i}`} />
                  <div>
                    <p className={`font-medium ${done.includes(i) ? "line-through text-slate-400" : ""}`}>{i + 1}. {s.title}</p>
                    <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
                    {s.link && <a href={s.link} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-[#1E3A8A] hover:underline" data-testid={`guide-step-link-${i}`}><ExternalLink className="h-3.5 w-3.5" /> Vai al portale</a>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <h2 className="text-lg font-medium mb-4">Fai da te o CAF?</h2>
            <Compare c={g.compare} />
            <p className="mt-3 text-xs text-slate-500">Valori indicativi, possono variare.</p>
          </div>
        </div>
        <div className="space-y-6">
          <div className="rounded-3xl border bg-white p-6">
            <h3 className="font-medium flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-[#1E3A8A]" /> Documenti da preparare</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">{g.documents.map((d) => <li key={d}>{d}</li>)}</ul>
          </div>
          <div className="rounded-3xl border bg-white p-6">
            <h3 className="font-medium">Portali ufficiali</h3>
            {g.portals.map((p) => <a key={p.url} href={p.url} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center gap-2 text-sm text-[#1E3A8A] hover:underline" data-testid={`guide-portal-${p.name}`}><ExternalLink className="h-3.5 w-3.5" /> {p.name}</a>)}
          </div>
          <Button asChild variant="outline" className="w-full rounded-full" data-testid="guide-ask-chat-btn"><Link to="/app/chat"><MessageCircle className="h-4 w-4 mr-2" /> Ho un dubbio, chiedo a Burocrazy</Link></Button>
        </div>
      </div>
    </div>
  );
}
